import apiClient from './client'

export const interviewerApi = {
  getProfile: () =>
    apiClient.get('/interviewer/profile').then((r) => r.data),

  updateProfile: (data: Record<string, unknown>) =>
    apiClient.put('/interviewer/profile', data).then((r) => r.data),

  getSchedule: () =>
    apiClient.get('/interviewer/schedule').then((r) => r.data),

  updateCalendly: (calendly_url: string) =>
    apiClient.put('/interviewer/schedule/calendly', { calendly_url }).then((r) => r.data),

  // GET /calendly/oauth/status
  // Response: { connected, calendly_user_uri, event_type_uri, event_type_name, event_type_duration, connected_at, last_sync_at }
  getCalendlyStatus: () =>
    apiClient
      .get<{
        connected: boolean
        calendly_user_uri: string | null
        event_type_uri: string | null
        event_type_name: string | null
        event_type_duration: number | null
        connected_at: string | null
        last_sync_at: string | null
      }>('/calendly/oauth/status')
      .then((r) => r.data),

  // GET /calendly/oauth/authorize
  // Response: { authorization_url: string }
  getCalendlyAuthUrl: () =>
    apiClient
      .get<{ authorization_url: string }>('/calendly/oauth/authorize')
      .then((r) => r.data),

  // GET /calendly/oauth/callback?code=&state=
  // Called by the frontend with JWT Bearer token after Calendly redirects to the React app
  connectCalendly: (code: string, state: string) =>
    apiClient
      .get('/calendly/oauth/callback', { params: { code, state } })
      .then((r) => r.data),

  // GET /calendly/event_types
  // Returns { event_types: [{ uri, name, duration, scheduling_url, active }] }
  getEventTypes: () =>
    apiClient.get('/calendly/event_types').then((r) => r.data),

  // PUT /calendly/event_type
  // Body: { event_type_uri: string } — full URI e.g. https://api.calendly.com/event_types/...
  setCalendlyEventType: (event_type_uri: string) =>
    apiClient
      .put('/calendly/event_type', { event_type_uri })
      .then((r) => r.data),

  // ── BACKEND REQUIREMENT ────────────────────────────────────────────────────
  // DELETE /interviewer/schedule/calendly
  // Should revoke the Calendly access_token and clear all stored tokens
  // for the authenticated interviewer. No request body needed.
  // Response: { message: 'Calendly disconnected' } or just 200/204
  // ──────────────────────────────────────────────────────────────────────────
  disconnectCalendly: () =>
    apiClient.delete('/calendly/oauth/disconnect').then((r) => r.data),

  updateAvailability: (slots: Record<string, unknown>[]) =>
    apiClient
      .put('/interviewer/schedule/availability', { slots })
      .then((r) => r.data),

  toggleAvailability: (is_available: boolean) =>
    apiClient
      .put('/interviewer/schedule/availability/toggle', { is_available })
      .then((r) => r.data),

  getMySchedule: () =>
    apiClient
      .get('/calendly/my-schedule')
      .then((r) => r.data.data ?? r.data),

  getClients: () =>
    apiClient.get('/interviewer/clients').then((r) => r.data.data ?? r.data),

  getClientQuestionSets: (clientUserId: number) =>
    apiClient.get(`/question-sets/client/${clientUserId}`).then((r) => r.data.data ?? r.data),

  getClientSessions: (clientId: number) =>
    apiClient.get(`/interviewer/sessions/${clientId}`).then((r) => r.data.data ?? r.data),

  getSessions: (params?: Record<string, unknown>) =>
    apiClient.get('/sessions', { params }).then((r) => r.data.data ?? r.data),

  getSession: (id: number) =>
    apiClient.get(`/sessions/${id}`).then((r) => r.data),

  getBooking: (id: number) =>
    apiClient.get(`/bookings/${id}`).then((r) => r.data.data ?? r.data),

  getStats: () =>
    apiClient.get('/stats').then((r) => r.data),

  getDashboard: () =>
    apiClient.get('/interviewer/dashboard').then((r) => r.data.data ?? r.data),

  cancelBooking: (bookingId: number, reason?: string) =>
    apiClient
      .put(`/interviewer/bookings/${bookingId}/cancel`, { reason })
      .then((r) => r.data),

  getClientPrepInfo: (clientUserId: number) =>
    apiClient
      .get(`/interviewer/clients/${clientUserId}`)
      .then((r) => r.data.data ?? r.data),

  getJdSignedUrl: (key: string) =>
    apiClient
      .get(`/calendly/job-description/signed-url`, { params: { key } })
      .then((r) => (r.data.data ?? r.data) as { signed_url: string }),

  generateQuestionsForClient: (
    clientUserId: number,
    data: { num_questions?: number; job_description?: string | null },
  ) =>
    apiClient
      .post(`/interviewer/clients/${clientUserId}/generate-questions`, data)
      .then((r) => r.data.data ?? r.data),

  saveQuestionSet: (data: { session_id: number; questions: object[]; is_finalized?: boolean }) =>
    apiClient
      .post('/question-sets', data)
      .then((r) => r.data.data ?? r.data),
}
